/**
 * useTenantUpgrade Hook
 *
 * Manages tenant upgrade operations: check availability, validate,
 * start upgrade, poll progress, rollback, and view history.
 *
 * @module features/tenants/hooks/useTenantUpgrade
 */

import { useState, useCallback, useRef } from 'react';
import {
  AvailableUpgrade,
  UpgradeValidation,
  UpgradeStatus,
  UpgradeHistoryItem,
  fetchAvailableUpgrades,
  validateUpgrade,
  startUpgrade,
  fetchUpgradeStatus,
  rollbackUpgrade,
  fetchUpgradeHistory,
} from '../api/upgradeApi';

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

const POLL_INTERVAL = 3000;
const MAX_POLLS = 120; // 6 minutes max

export interface UseTenantUpgradeResult {
  available: AvailableUpgrade | null;
  validation: UpgradeValidation | null;
  upgradeStatus: UpgradeStatus | null;
  history: UpgradeHistoryItem[];
  loading: boolean;
  error: string | null;
  checkAvailable: (tenantId: string) => Promise<AvailableUpgrade | null>;
  validate: (tenantId: string, targetVersion: string) => Promise<UpgradeValidation | null>;
  startTenantUpgrade: (tenantId: string, targetVersion: string) => Promise<void>;
  rollback: (tenantId: string, reason?: string) => Promise<void>;
  loadHistory: (tenantId: string) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

export function useTenantUpgrade(authFetch: AuthFetch): UseTenantUpgradeResult {
  const [available, setAvailable] = useState<AvailableUpgrade | null>(null);
  const [validation, setValidation] = useState<UpgradeValidation | null>(null);
  const [upgradeStatus, setUpgradeStatus] = useState<UpgradeStatus | null>(null);
  const [history, setHistory] = useState<UpgradeHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    setAvailable(null);
    setValidation(null);
    setUpgradeStatus(null);
    setHistory([]);
    setError(null);
    stopPolling();
  }, [stopPolling]);

  const checkAvailable = useCallback(async (tenantId: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAvailableUpgrades(authFetch, tenantId);
      setAvailable(data);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check upgrades');
      return null;
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  const validate = useCallback(async (tenantId: string, targetVersion: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await validateUpgrade(authFetch, tenantId, targetVersion);
      setValidation(data);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Validation failed');
      return null;
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  const startTenantUpgrade = useCallback(async (tenantId: string, targetVersion: string) => {
    setLoading(true);
    setError(null);
    try {
      const status = await startUpgrade(authFetch, tenantId, targetVersion);
      setUpgradeStatus(status);

      // Start polling for progress
      let pollCount = 0;
      stopPolling();
      pollRef.current = setInterval(async () => {
        pollCount++;
        if (pollCount >= MAX_POLLS) {
          stopPolling();
          setError('Upgrade timed out');
          setLoading(false);
          return;
        }
        try {
          const updated = await fetchUpgradeStatus(authFetch, tenantId);
          setUpgradeStatus(updated);
          const terminal = ['completed', 'failed', 'rolled_back'];
          if (terminal.includes(updated.status)) {
            stopPolling();
            setLoading(false);
          }
        } catch {
          // Continue polling on transient errors
        }
      }, POLL_INTERVAL);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start upgrade');
      setLoading(false);
    }
  }, [authFetch, stopPolling]);

  const rollbackFn = useCallback(async (tenantId: string, reason?: string) => {
    setLoading(true);
    setError(null);
    try {
      await rollbackUpgrade(authFetch, tenantId, reason);
      // Poll once to get updated status
      const updated = await fetchUpgradeStatus(authFetch, tenantId);
      setUpgradeStatus(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rollback failed');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  const loadHistory = useCallback(async (tenantId: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchUpgradeHistory(authFetch, tenantId);
      setHistory(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  return {
    available,
    validation,
    upgradeStatus,
    history,
    loading,
    error,
    checkAvailable,
    validate,
    startTenantUpgrade,
    rollback: rollbackFn,
    loadHistory,
    clearError,
    reset,
  };
}
